import * as notify from "../notify";
import { DEFAULT_GENERIC_ERROR } from "../constants";
import { sendNote } from "../../components/notes/lib/data";
import { useCallback, useContext, useState } from "react";
import { redoTask } from "../../api/left-rail-menu";
import { completeTask } from "../taskState";
import { TaskContext } from "../../contexts/task";
import { SECTION_MAP } from "../../components/task-list/lib/constants";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { TaskName } from "shared/constants/task-name";
import { PermissionSource } from "shared/constants/permission-source";
import { getRequestOriginTaskType } from "shared/constants/request-origin-task-type";

const COMPLETE_ON_PHOTOGRAPHY_REDO = [TaskName.IMAGE_EDITING, TaskName.IMAGE_EDITING_QA];

export const useRedo = (taskName, sourcePage) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { task } = useContext(TaskContext);
    const [isProcessing, setProcessing] = useState(false);

    const redo = useCallback(async () => {
        setProcessing(true);

        try {
            const prompt = await notify.prompt("Reason for redo:");

            if (prompt.length <= 0) {
                notify.error("A reason must be provided to redo a task");

                return;
            }

            if (!prompt) {
                return;
            }

            const isLeftRailPhotographyRedo = taskName === TaskName.PHOTOGRAPHY && sourcePage === PermissionSource.LEFT_RAIL;
            const requestOriginTaskType = isLeftRailPhotographyRedo ? getRequestOriginTaskType(task.sourcePage) : undefined;
            const taskRedoTask = await redoTask(taskName, task.taskContextKey, sourcePage, requestOriginTaskType);

            if (!taskRedoTask.success) {
                // This is how the errors are reported to the system, so let's show something meaningful here
                notify.error(taskRedoTask[0] || DEFAULT_GENERIC_ERROR);

                return;
            }

            const noteTaskType = SECTION_MAP[taskName].noteKey;
            const rejectComment = await sendNote({
                items: [
                    {
                        noteText: prompt,
                        noteType: "Product",
                        shipmentKey: task.context.shipmentKey,
                        shipmentProductKey: task.context.shipmentProductKey,
                        taskType: noteTaskType,
                    },
                ],
            });

            if (!rejectComment) {
                return;
            }

            const shouldCompleteTask = taskName === TaskName.PHOTOGRAPHY && COMPLETE_ON_PHOTOGRAPHY_REDO.includes(task.sourcePage);

            if (shouldCompleteTask) {
                const completionResponse = await completeTask(task.key, true);

                if (!completionResponse) {
                    return;
                }
            }

            notify.success("Task has been marked for redo");

            if (taskName === TaskName.MEASURING) {
                navigate(`/shipment-editor/${task.context.shipmentKey}#measurements`);
            } else if (shouldCompleteTask) {
                navigate(`/task-list/${task.sourcePage}`);
            } else if (taskName === TaskName.PHOTOGRAPHY) {
                queryClient.invalidateQueries(["leftRailMenu", task.context.shipmentProductKey]);
            } else {
                navigate(`/task-list/${taskName}`);
            }
        } finally {
            setProcessing(false);
        }
    }, [
        taskName,
        sourcePage,
        queryClient,
        task.key,
        task.sourcePage,
        task.taskContextKey,
        task.context.shipmentKey,
        task.context.shipmentProductKey,
        navigate,
    ]);

    return [isProcessing, redo];
};
